import { useState } from 'react'
import { useSession } from '@wiggot/auth/hooks/useSession'
import type { InputType } from '@dd3tech/wiggui/Input'
import { toast } from '@dd3tech/wiggui/Toast'
import { useModalCompleteProfileStore } from '../../../../../store'
import { updateCookieProfile } from '../../../../../utils'
import { patchUpdateProfile } from '../../../../../services'

export type FormInputs = FormInput[]

export interface FormInput {
  label: string
  value: string | null
  name: string
  type: InputType
  isError: boolean
  onChange: (value: string) => void
  className?: string
  phoneCodeValue?: string | null
  onChangePhoneCode?: (value: string) => void
  isDisabled?: boolean
}

export const useCompleteProfile = () => {
  const { profile, updateProfile } = useSession()
  const [isLoading, setIsLoading] = useState(false)
  const isOpen = useModalCompleteProfileStore((state) => state.isOpen)
  const setIsOpen = useModalCompleteProfileStore((state) => state.setIsOpen)
  const step = useModalCompleteProfileStore((state) => state.step)
  const setStep = useModalCompleteProfileStore((state) => state.setStep)

  const fullname = useModalCompleteProfileStore((state) => state.form.fullname)
  const email = useModalCompleteProfileStore((state) => state.form.email)
  const phoneNumber = useModalCompleteProfileStore(
    (state) => state.form.phoneNumber
  )
  const phoneCode = useModalCompleteProfileStore(
    (state) => state.form.phoneCode
  )

  const getPhoneNumber = useModalCompleteProfileStore(
    (state) => state.getPhoneNumber
  )

  const setForm = useModalCompleteProfileStore((state) => state.setForm)

  const setProfileIsUpdated = useModalCompleteProfileStore(
    (state) => state.setProfileIsUpdated
  )

  const titleByStep =
    step === 1
      ? 'Falta información importante en tu perfil'
      : 'Información personal'

  const formInputs: FormInputs = [
    {
      label: 'Nombre completo*',
      value: fullname,
      name: 'fullname',
      type: 'text',
      isError: !fullname || fullname?.toString()?.trim()?.length < 3,
      onChange: (value: string) => {
        setForm({ fullname: value })
      }
    },
    {
      label: 'Correo electrónico*',
      value: email,
      name: 'email',
      type: 'text',
      isError: false,
      isDisabled: true,
      onChange: (value: string) => {
        setForm({ email: value })
      }
    },
    {
      label: 'Teléfono*',
      value: phoneNumber,
      type: 'phone-number',
      phoneCodeValue: phoneCode,
      onChangePhoneCode: (value: string) => {
        setForm({ phoneCode: value })
      },
      name: 'phone',
      isError: !phoneNumber || phoneNumber?.toString()?.trim()?.length < 8,
      onChange: (value: string) => {
        setForm({ phoneNumber: value })
      }
    }
  ]

  const handleOnSubmit = async () => {
    const formData = {
      user_fullname: fullname,
      user_email: email,
      user_phone_number: getPhoneNumber()
    }

    try {
      setIsLoading(true)
      const { isSuccess } = await patchUpdateProfile(formData)

      if (isSuccess) {
        toast({
          type: 'success',
          title: 'Información guardada'
        })

        if (profile) {
          const newProfile = updateCookieProfile(profile, formData)
          setProfileIsUpdated(true)
          updateProfile(newProfile)
        }

        setIsOpen({ isOpen: false })

        return
      }
      toast({
        type: 'error',
        title: 'Ocurrió un error al actualizar tu perfil',
        description: 'Por favor, intenta nuevamente'
      })
    } catch {
      toast({
        type: 'error',
        title: 'Ocurrió un error al actualizar tu perfil',
        description: 'Por favor, intenta nuevamente'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const buttonSavedIsDisabled = Boolean(
    formInputs?.some((input) => {
      return input?.isError
    })
  )

  return {
    isOpen,
    setIsOpen,
    step,
    setStep,
    titleByStep,
    formInputs,
    handleOnSubmit,
    buttonSavedIsDisabled,
    isLoading
  }
}
